import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getProperty } from '../lib/property-config.js';
import { CONTACT_EMAIL } from '../lib/site-config.js';
import { fetchBooking } from '../lib/booking-api.js';
import Footer from '../components/Footer.jsx';
import Header from '../components/Header.jsx';
import Seo from '../components/Seo.jsx';
import { track } from '../lib/analytics.js';
import { claimBookingConfirmedEvent, hasBookingHandoff } from '../lib/booking-handoff.js';

const POLL_INTERVAL = 2000;
const POLL_TIMEOUT = 60000;

export default function StayConfirmed() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const property = getProperty(slug);

  // Path A (OwnerRez hosted payment form) redirects the guest back here after a
  // successful booking, WITHOUT a Stripe session_id. In PAT mode we have no
  // webhook to reconcile against, so we show a warm confirmation and let the
  // OwnerRez email carry the receipt. The legacy session_id path (Stripe / a
  // future Path B) still polls the booking record.
  const sessionId = params.get('session_id');
  // OwnerRez may append a booking reference on the redirect; surface it if so.
  const bookingRef =
    params.get('booking_id') || params.get('bookingId') || params.get('id') || params.get('guid');

  // Evidence that this visit follows a payment attempt, rather than a bookmark,
  // a Back press off the abandoned payment form, or a link someone was sent.
  // Either the booking reference OwnerRez may append, or the marker this tab
  // wrote on its way out to the payment page. Read once, on mount, because
  // sessionStorage is not reactive and the answer cannot change mid-visit.
  const [handedOff] = useState(() => hasBookingHandoff(slug));
  const hasEvidence = Boolean(bookingRef) || handedOff;

  // Without evidence the page claims nothing. Saying "payment went through" to
  // someone who never paid costs them the reservation: they stop watching for
  // the confirmation email and arrive with no booking.
  const [status, setStatus] = useState(
    sessionId ? 'pending' : hasEvidence ? 'received' : 'unverified'
  );
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  // Funnel end. Gated on the same evidence, and claimed once, so a refresh or
  // a cold visit cannot inflate the only booking count the owner has.
  useEffect(() => {
    if (sessionId || !hasEvidence) return;
    if (claimBookingConfirmedEvent(slug)) track('booking_confirmed', { slug });
  }, [slug, sessionId, hasEvidence]);

  useEffect(() => {
    if (!sessionId) return; // Path A: nothing to poll.

    let cancelled = false;
    const startedAt = Date.now();

    async function poll() {
      try {
        const res = await fetchBooking(sessionId);
        if (cancelled) return;
        if (res.status === 'confirmed') {
          setStatus('confirmed');
          setBooking(res.booking);
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
      }
    }

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const ConciergeNote = (
    <>
      <p className="mt-6 text-navy-700">
        Your Charleston Concierge, a personalized stay guide for your dates,
        unlocks 7 days before arrival.
      </p>
      <p className="mt-3 text-navy-700">
        In the meantime,{' '}
        <Link to={`/stay/${slug}/guide`} className="text-navy-800 underline">
          the welcome guide
        </Link>{' '}
        covers the home, check-in basics, and our local recommendations. Door codes
        and Wi-Fi arrive in your check-in message.
      </p>
    </>
  );

  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6">
      <Seo
        title={
          status === 'unverified'
            ? 'Booking confirmation · Charleston.Vacations'
            : 'Booking confirmed · Charleston.Vacations'
        }
        noindex
      />
      <Header />

      {/* No evidence of a payment attempt: a bookmark, a shared link, or a Back
          press off the abandoned payment form. State what is knowable and
          nothing more, and give them a way to finish or to ask. */}
      {status === 'unverified' && (
        <div className="pt-8">
          <h1 className="font-display text-3xl font-semibold text-navy-900">Booking confirmation</h1>
          <p className="mt-2 text-navy-600">{property?.name}</p>
          <p className="mt-4 text-navy-700">
            If you just completed payment, your confirmation email is on its way,
            with your full itinerary and receipt. It usually arrives within a few
            minutes; check spam if you don't see it.
          </p>
          <p className="mt-3 text-navy-700">
            We can't confirm a booking from this page on its own. If you're not
            sure your payment went through,{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-navy-800 underline">
              email us
            </a>{' '}
            and we'll check the calendar for you, or{' '}
            <Link to={`/stay/${slug}`} className="text-navy-800 underline">
              pick your dates again
            </Link>
            .
          </p>
        </div>
      )}

      {/* Path A: redirected back from the OwnerRez payment form. */}
      {status === 'received' && (
        <div className="pt-8">
          <h1 className="font-display text-3xl font-semibold text-navy-900">You're booked.</h1>
          <p className="mt-2 text-navy-600">
            {property?.name}
            {bookingRef ? <> · booking <span className="font-mono">{bookingRef}</span></> : null}
          </p>
          <p className="mt-4 text-navy-700">
            Payment went through and your dates are locked on our calendar, the same
            one Airbnb and Vrbo sync from. A confirmation email with your full
            itinerary and receipt is on its way. If you don't see it in a few minutes,
            check spam or email us and we'll resend it.
          </p>
          {ConciergeNote}
        </div>
      )}

      {/* Pending, timeout and error reuse the received state's heading, subline
          and measure, so the page reads as one page at different moments rather
          than a centred notice on an empty page. */}
      {status === 'pending' && !error && (
        <div className="pt-8">
          <h1 className="font-display text-3xl font-semibold text-navy-900">Confirming your booking…</h1>
          <p className="mt-2 text-navy-600">{property?.name}</p>
          <p className="mt-4 text-navy-700">Hang tight. This usually takes a few seconds.</p>
        </div>
      )}

      {status === 'timeout' && (
        <div className="pt-8">
          <h1 className="font-display text-3xl font-semibold text-navy-900">Still working on it.</h1>
          <p className="mt-2 text-navy-600">{property?.name}</p>
          <p className="mt-4 text-navy-700">
            Your payment went through but the booking confirmation is taking longer than usual.
            We have your details and will email you within an hour. Reach out if you don't hear back.
          </p>
          {ConciergeNote}
        </div>
      )}

      {error && (
        <div className="pt-8">
          <h1 className="font-display text-3xl font-semibold text-navy-900">Something went sideways.</h1>
          <p className="mt-2 text-navy-600">{property?.name}</p>
          <p className="mt-4 text-charlestonpink-600">{error}</p>
        </div>
      )}

      {status === 'confirmed' && booking && (
        <div className="pt-8">
          <h1 className="font-display text-3xl font-semibold text-navy-900">You're booked.</h1>
          <p className="mt-2 text-navy-600">
            {property?.name} · {booking.arrival} → {booking.departure}
          </p>
          <div className="mt-6 rounded-xl border border-navy-100 bg-cream-50 p-4 text-sm">
            <div className="flex justify-between">
              <span className="text-navy-500">Booking ID</span>
              <span className="font-mono text-navy-900">{booking.ownerRezBookingId || booking.id}</span>
            </div>
            <div className="mt-2 flex justify-between">
              <span className="text-navy-500">Guest</span>
              <span>{booking.guest.firstName} {booking.guest.lastName}</span>
            </div>
            <div className="mt-2 flex justify-between">
              <span className="text-navy-500">Email</span>
              <span>{booking.guest.email}</span>
            </div>
            <div className="mt-2 flex justify-between">
              <span className="text-navy-500">Total</span>
              <span className="font-semibold">${booking.totalAmount?.toFixed(2)}</span>
            </div>
          </div>
          {ConciergeNote}
        </div>
      )}

      <Footer />
    </div>
  );
}
